type RefreshCallback = () => Promise<void> | void;

interface RefreshCallbackConfig {
  id: string;
  callback: RefreshCallback;
  priority: number; // Lower number = higher priority
}

class PostLoginRefreshManager {
  private static instance: PostLoginRefreshManager;
  private refreshCallbacks: Map<string, RefreshCallbackConfig> = new Map();
  private isRefreshing: boolean = false;
  private listeners: Set<(isRefreshing: boolean) => void> = new Set();

  private constructor() {}

  static getInstance(): PostLoginRefreshManager {
    if (!PostLoginRefreshManager.instance) {
      PostLoginRefreshManager.instance = new PostLoginRefreshManager();
    }
    return PostLoginRefreshManager.instance;
  }

  register(id: string, callback: RefreshCallback, priority: number = 100): void {
    this.refreshCallbacks.set(id, { id, callback, priority });
  }

  unregister(id: string): void {
    this.refreshCallbacks.delete(id);
  }

  async executeAll(): Promise<void> {
    if (this.isRefreshing) {
      console.log('Refresh already in progress, skipping...');
      return;
    }

    this.isRefreshing = true;
    this.notifyListeners();

    console.log('Starting post-login refresh with', this.refreshCallbacks.size, 'callbacks');

    try {
      // Sort callbacks by priority
      const sortedCallbacks = Array.from(this.refreshCallbacks.values())
        .sort((a, b) => a.priority - b.priority);

      // Execute callbacks in priority order
      for (const config of sortedCallbacks) {
        try {
          console.log(`Executing refresh callback: ${config.id}`);
          await Promise.resolve(config.callback());
        } catch (error) {
          console.error(`Error in refresh callback ${config.id}:`, error);
        }
      }
    } finally {
      this.isRefreshing = false;
      this.notifyListeners();
      console.log('Post-login refresh completed');
    }
  }

  subscribe(listener: (isRefreshing: boolean) => void): () => void {
    this.listeners.add(listener);
    listener(this.isRefreshing); // Send current state immediately
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.isRefreshing));
  }

  getIsRefreshing(): boolean {
    return this.isRefreshing;
  }
}

export const postLoginRefreshManager = PostLoginRefreshManager.getInstance();